import { useContext, useEffect, useMemo, useState } from "react";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { authorized, RESOURCE_NAME } from "common/authorization";
import { sourceForGroupId } from "common/utils/queryBuilder";
import {
  type BrokenSavedView,
  getBrokenSavedViews,
  getRemovedFields,
} from "common/utils/savedViews";
import { CustomObjectGroup } from "common-client/generated/graphql";
import {
  CreateCustomObjectGroupInput,
  CreateCustomObjectGroupMutationFn,
  UpdateCustomObjectGroupInput,
  UpdateCustomObjectGroupMutationFn,
  useGetSavedViewsQuery,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { track } from "../../../../utils/tracking";
import { AuthContext } from "../../../Authorization/AuthContext";
import BrokenSavedViews from "../../../Common/BrokenSavedViews";
import { CommonModal } from "../../../Common/CommonModal";
import Modal from "../../../Common/Modal";
import { Select } from "../../../Inputs";
import { Checkbox, Text } from "../../../Inputs/react-hook-form";
import { InputGrid } from "../PropertyData/__styles__/floodInfoModals";
import { UpsertModalContent } from "./__styles__/CustomObjectGroup";

type UpsertCustomObjectGroupForm = {
  name: string;
  hasTable: boolean;
  customMapIds: Array<string>;
};
export type CreateCustomObjectGroupFunc = (
  data: CreateCustomObjectGroupInput,
) => Promise<void>;
export type UpdateCustomObjectGroupFunc = (
  data: UpdateCustomObjectGroupInput,
) => Promise<void>;

const textByUpsertType = {
  create: {
    title: "Add group",
    primaryButtonText: "Add",
  },
  edit: {
    title: "Edit group",
    primaryButtonText: "Save",
  },
};

export const UpsertCustomObjectGroupModal = ({
  hideModal,
  customObjectGroup,
  onSave,
  refetch,
}: {
  hideModal: () => void;
  customObjectGroup?: Maybe<CustomObjectGroup>;
  onSave: CreateCustomObjectGroupMutationFn | UpdateCustomObjectGroupMutationFn;
  refetch: () => void;
}) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
    formState: { isValid },
  } = useForm<UpsertCustomObjectGroupForm>({
    defaultValues: {
      name: customObjectGroup?.name,
      hasTable: customObjectGroup?.hasTable ?? false,
      customMapIds: customObjectGroup?.customMapIds,
    },
  });

  const [brokenSavedViews, setBrokenSavedViews] = useState<
    Array<BrokenSavedView>
  >([]);
  const [breakingCustomMaps, setBreakingCustomMaps] = useState<string[]>([]);

  const newCustomMapIds = watch("customMapIds");

  const tableSource =
    customObjectGroup ? sourceForGroupId(customObjectGroup.id) : undefined;

  const { data: savedViewsResponse } = useGetSavedViewsQuery({
    variables: tableSource,
    fetchPolicy: "cache-and-network",
    skip: !customObjectGroup,
  });

  const customObjects = useMemo(
    () => account?.customMaps.filter(map => map.isObject) ?? [],
    [account],
  );

  const removedCustomMapIds = useMemo(() => {
    return (
      customObjectGroup?.customMapIds.filter(
        id => !newCustomMapIds?.includes(id),
      ) ?? []
    );
  }, [customObjectGroup, newCustomMapIds]);

  useEffect(() => {
    if (
      savedViewsResponse?.account?.savedViews &&
      removedCustomMapIds.length > 0
    ) {
      const savedViews = savedViewsResponse.account.savedViews;
      const removedCustomMaps = customObjects?.filter(map =>
        removedCustomMapIds.includes(map.id),
      );
      const removedFields = getRemovedFields(removedCustomMaps);

      const { brokenSavedViews, breakingCustomMaps } = getBrokenSavedViews({
        savedViews,
        removedFields,
      });

      setBrokenSavedViews(brokenSavedViews);
      setBreakingCustomMaps(breakingCustomMaps);
    } else {
      setBrokenSavedViews([]);
      setBreakingCustomMaps([]);
    }
  }, [removedCustomMapIds, savedViewsResponse, customObjects]);

  const onSubmit = async (
    formData: UpsertCustomObjectGroupForm,
    type: "create" | "edit",
  ) => {
    if (type === "create") {
      const data = {
        ...formData,
        accountId: account!.id,
      };
      await (onSave as CreateCustomObjectGroupMutationFn)({
        variables: { data },
        onCompleted: async () => {
          track("Created custom object group", {
            name: formData.name,
          });
          addSuccessToast("Your object group was successfully created");
          refetch();
          hideModal();
        },
        onError: () => {
          addErrorToast(
            `There was an issue creating your object group. Please try again. If the problem persists, please email us at support@withforerunner.com`,
          );
        },
      });
    } else {
      const data = {
        ...formData,
        id: customObjectGroup!.id,
      };
      await (onSave as UpdateCustomObjectGroupMutationFn)({
        variables: { data },
        onCompleted: async () => {
          track("Updated object group", {
            name: formData.name,
          });
          addSuccessToast("Your object group was successfully updated");
          refetch();
          hideModal();
        },
        onError: () => {
          addErrorToast(
            "There was an issue updating your group. Please try again. If the problem persists, please email us at support@withforerunner.com",
          );
        },
      });
    }
  };

  const text =
    customObjectGroup ? textByUpsertType.edit : textByUpsertType.create;

  const canUsePrimaryButton = authorized({
    resource: RESOURCE_NAME.CUSTOM_OBJECT_GROUP,
    permission: customObjectGroup ? "update" : "create",
    entity: {
      role: "admin",
      canViewPersonalIdentifiableInformation: false,
    },
  });

  return (
    <CommonModal
      title={text.title}
      primaryButtonText={text.primaryButtonText}
      onSubmit={handleSubmit(
        formData =>
          void onSubmit(formData, customObjectGroup ? "edit" : "create"),
      )}
      disabled={!isValid || !canUsePrimaryButton}
      onCancel={hideModal}
      size="large"
      content={
        <UpsertModalContent>
          <InputGrid>
            <Text
              label="Name"
              error={errors.name?.message}
              {...register("name", {
                required: "Name is required",
              })}
              required
              data-testid="name-input"
            />
            <Controller
              name="customMapIds"
              control={control}
              rules={{ required: "Object types are required" }}
              render={({ field, fieldState }) => (
                <Select
                  name="customMapIds"
                  label="Object types"
                  options={
                    customObjects?.map(map => ({
                      label: map.name,
                      value: map.id,
                    })) ?? []
                  }
                  onChange={value => field.onChange(value ?? [])}
                  value={field.value}
                  placeholder="Select..."
                  size="medium"
                  required
                  isMulti
                  error={fieldState.error?.message}
                />
              )}
            />
            <Checkbox
              name="hasTable"
              control={control}
              label="Show an object table for this group"
              data-testid="show-object-table-input"
            />
          </InputGrid>
          {brokenSavedViews && (
            <BrokenSavedViews
              brokenSavedViews={brokenSavedViews}
              breakingCustomMaps={breakingCustomMaps}
            />
          )}
        </UpsertModalContent>
      }
    />
  );
};

export const useUpsertCustomObjectGroupModal = ({
  existingCustomObjectGroup,
  onSubmit,
  refetch,
}: {
  existingCustomObjectGroup?: Maybe<CustomObjectGroup>;
  onSubmit:
    | CreateCustomObjectGroupMutationFn
    | UpdateCustomObjectGroupMutationFn;
  refetch: () => void;
}) => {
  const [customObjectGroup, setCustomObjectGroup] = useState<
    Maybe<CustomObjectGroup>
  >(existingCustomObjectGroup ?? null);

  let [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <UpsertCustomObjectGroupModal
          hideModal={hideModal}
          customObjectGroup={customObjectGroup}
          onSave={onSubmit}
          refetch={refetch}
        />
      </Modal>
    ),
    [customObjectGroup],
  );

  const showUpsertModal = (customObjectGroup: Maybe<CustomObjectGroup>) => {
    setCustomObjectGroup(customObjectGroup);
    showModal();
  };

  return [showUpsertModal, hideModal] as const;
};
